// @unocss-include
import Layout from '@/components/layout/sider-menu-layout/index.vue';
import type { SiteRouteItem } from '@/router/types';
import { PermissionPageKey } from '@/types/permission';

const route: SiteRouteItem = {
  path: '/precision-new',
  component: Layout,
  name: PermissionPageKey.PrecisionNew,
  redirect: '/precision-new/pytest/projects',
  meta: {
    title: 'precision.new.title',
    icon: 'mingcute:target-fill',
    iconCls: 'bg-cyan-500',
    // ctc-ai 停库期间：精准测试(新)整站仅管理员可见（恢复后可回退到页面权限系统控制，见旧注释）
    // 原注释：已移除角色限制，改为使用页面权限系统控制访问
    roles: ['admin'],
  },
  children: [
    {
      path: 'feishu-document',
      name: PermissionPageKey.PrecisionNewFeishuDocument,
      component: () => import('../pages/feishu-document/Documents.vue'),
      meta: { title: 'precision.new.feishu.documents', icon: 'ph:file-text', keepAlive: true },
    },
    {
      path: 'feishu-document/document-analyze/:documentId',
      name: PermissionPageKey.PrecisionNewDocumentAnalyze,
      component: () => import('../pages/feishu-document/DocumentAnalyze.vue'),
      meta: { title: 'precision.new.document.analyze', icon: 'ph:file-text', hideInMenu: true },
    },
    {
      path: 'knowledge-graph',
      name: PermissionPageKey.PrecisionNewKnowledgeGraph,
      component: () => import('../pages/feishu-document/KnowledgeGraph.vue'),
      meta: { title: 'precision.new.knowledge.graph', icon: 'ph:graph-fill', keepAlive: true },
    },
    {
      path: 'knowledge-graph/:documentId',
      name: PermissionPageKey.PrecisionNewKnowledgeGraphForDocument,
      component: () => import('../pages/feishu-document/KnowledgeGraphForDocument.vue'),
      meta: { title: 'precision.new.knowledge.graph', icon: 'ph:graph-fill', hideInMenu: true },
    },
    // 覆盖率统计列表已下线
    {
      path: 'coverage-report/:id',
      name: PermissionPageKey.PrecisionNewCoverageReportDetail,
      component: () => import('../pages/coverage-report/detail.vue'),
      meta: { title: 'precision.new.coverage.report', icon: 'ph:graph-fill', hideInMenu: true },
    },
    {
      path: 'analyzing',
      name: PermissionPageKey.PrecisionNewAnalyzing,
      component: () => import('../pages/analyzing/index.vue'),
      meta: { title: 'precision.new.ai.analyzing', icon: 'ph:activity-bold', hideInMenu: true },
    },
    {
      path: 'case-recommend',
      name: PermissionPageKey.PrecisionNewCaseRecommend,
      component: () => import('../pages/case-recommend/index.vue'),
      meta: { title: 'precision.new.case.recommend', icon: 'mdi:lightbulb-on', hideInMenu: true },
    },
    {
      path: 'test-plan',
      name: PermissionPageKey.PrecisionNewTestPlan,
      component: () => import('../pages/test-plan/index.vue'),
      meta: { title: 'project.dev.test_plan.title', icon: 'ph:clipboard-text', keepAlive: true },
    },
    {
      path: 'test-plan/detail/:project/:versionId?/:id?',
      name: PermissionPageKey.PrecisionNewTestPlanDetail,
      component: () => import('../pages/test-plan/detail.vue'),
      meta: { title: 'project.dev.test_plan_detail.title', hideInMenu: true },
    },
    {
      path: 'test-case-generation',
      name: PermissionPageKey.PrecisionNewTestCaseGeneration,
      component: () => import('@/sites/precision-new/pages/test-case-generation/mindmap-testcase.vue'),
      meta: { title: 'project.dev.testcase.title', icon: 'tabler:test-pipe', keepAlive: true },
    },
    // 代码绑定与影响分析
    {
      path: 'code-binding',
      name: PermissionPageKey.PrecisionNewCodeBinding,
      component: () => import('../pages/code-binding/index.vue'),
      meta: { title: 'precision.new.code.binding', icon: 'ph:link', keepAlive: true },
    },
    {
      path: 'impact-analysis',
      name: PermissionPageKey.PrecisionNewImpactAnalysis,
      component: () => import('../pages/impact-analysis/index.vue'),
      meta: { title: 'precision.new.impact.analysis', icon: 'ph:graph', keepAlive: true },
    },
    // Pytest 相关路由
    {
      path: 'pytest/projects',
      name: PermissionPageKey.PytestProjects,
      component: () => import('../pages/pytest/projects/index.vue'),
      meta: { title: 'pytest.projects.title', icon: 'ph:folder', keepAlive: true },
    },
    {
      path: 'pytest/projects/all-reports',
      name: PermissionPageKey.PytestAllReports,
      component: () => import('../pages/pytest/projects/all-reports.vue'),
      meta: { title: 'pytest.all.reports', hideInMenu: true },
    },
    {
      path: 'pytest/projects/:projectId',
      name: PermissionPageKey.PytestProjectDetail,
      component: () => import('../pages/pytest/projects/project/detail.vue'),
      meta: { title: 'pytest.project.detail', hideInMenu: true },
      redirect: { name: PermissionPageKey.PytestProjectCases },
      children: [
        {
          path: 'cases',
          name: PermissionPageKey.PytestProjectCases,
          component: () => import('../pages/pytest/projects/project/cases.vue'),
          meta: { title: 'pytest.project.cases', hideInMenu: true },
        },
        {
          path: 'editor',
          name: PermissionPageKey.PytestProjectEditor,
          component: () => import('../pages/pytest/projects/project/editor.vue'),
          meta: { title: 'pytest.project.editor', hideInMenu: true },
        },
        {
          path: 'execute',
          name: PermissionPageKey.PytestProjectExecute,
          component: () => import('../pages/pytest/projects/project/execute.vue'),
          meta: { title: 'pytest.project.execute', hideInMenu: true },
        },
        {
          path: 'reports',
          name: PermissionPageKey.PytestReports,
          component: () => import('../pages/pytest/projects/project/reports/index.vue'),
          meta: { title: 'pytest.project.reports', hideInMenu: true },
        },
        {
          path: 'reports/:executionId',
          name: PermissionPageKey.PytestReportDetail,
          component: () => import('../pages/pytest/projects/project/reports/detail.vue'),
          meta: { title: 'pytest.report.detail', hideInMenu: true },
        },
        {
          path: 'artifacts',
          name: PermissionPageKey.PytestReceivedArtifacts,
          component: () => import('../pages/pytest/projects/project/received-artifacts.vue'),
          meta: { title: 'pytest.received.artifacts', hideInMenu: true },
        },
      ],
    },
  ],
};

export default route;
